import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Editor, Entity, EditorState, RichUtils, CompositeDecorator } from 'draft-js'
import autobind from 'autobind-decorator'
import { toContentState, fromContentState } from './state'
import { getSelectionRange, getSelectedBlockElement, getSelectionCoords } from './selection'
import SideToolbar from './toolbar'
import InlineToolbar from './inlinetoolbar'
import LinkEditor from './linkeditor'
import LinkContainer from 'redux/containers/components/shared/texteditor/LinkContainer'
import { PRECEDENTS_CONTENT } from 'constants/controllers'

const findLinkEntities = function (contentBlock, callback) {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity()
    return (entityKey !== null && Entity.get(entityKey).getType() === 'LINK')
  }, callback)
}

export default class TextEditor extends Component {
  static defaultProps = {
    value: '',
    controller: null,
    placeholder: '',
    onChange: () => {},
    blockTypes: [
      { icon: 'icon-format-header-1', style: 'header-one' },
      { icon: 'icon-format-header-2', style: 'header-two' },
      { icon: 'icon-format-list-bulleted', style: 'unordered-list-item' },
      { icon: 'icon-format-list-numbers', style: 'ordered-list-item' },
      { icon: 'icon-format-quote', style: 'blockquote' }
    ]
  }

  static propTypes = {
    value: PropTypes.string,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
    blockTypes: PropTypes.arrayOf(PropTypes.shape({
      icon: PropTypes.string.isRequired,
      style: PropTypes.string.isRequired
    })),
    controller: PropTypes.string
  }

  constructor (props) {
    super(props)
    this.containerRef = React.createRef()
    this.editorRef = React.createRef()
    const decorator = new CompositeDecorator([
      {
        strategy: findLinkEntities,
        component: LinkContainer
      }
    ])

    this.state = {
      editorState: EditorState.createWithContent(toContentState(props.value), decorator),
      inlineToolbar: {
        show: false
      },
      selectedBlock: null,
      selectionRange: null
    }
  }

  componentDidMount () {
    const { setSelectionFunction } = this.props
    setSelectionFunction && setSelectionFunction(this.selectBlock)
  }

  componentDidUpdate (prevProps, preState, _) {
    if (prevProps.value !== this.props.value && this.props.controller === PRECEDENTS_CONTENT) {
      this.setState({
        editorState: EditorState.moveFocusToEnd(EditorState.createWithContent(toContentState(this.props.value), this.decorator))
      })
    }
  }

  @autobind
  selectBlock (el) {
    const selection = window.getSelection()
    selection.removeAllRanges()
    const range = document.createRange()
    range.setStart(el, 0)
    range.setEnd(el, 1)
    selection.addRange(range)
  }

  @autobind
  blockRenderer (block) {
    // if (block.getType() === 'atomic') {
    //     return { component: ImageComponent };
    // }
    return null
  }

  @autobind
  blockStyler (block) {
    if (block.getType() === 'unstyled') {
      return 'paragraph'
    }
    return null
  }

  @autobind
  onChange (editorState) {
    this.updateInlineToolbarPosition(editorState)
    this.props.onChange(fromContentState(editorState.getCurrentContent()))
    this.setState({ editorState })
    setTimeout(this.updateSelection, 0)
  }

  @autobind
  updateInlineToolbarPosition (editorState = this.state.editorState) {
    if (editorState && !editorState.getSelection().isCollapsed()) {
      const selectionRange = getSelectionRange()
      const { offsetLeft, offsetTop } = getSelectionCoords(this.containerRef.current, selectionRange)

      if (offsetTop && offsetLeft) {
        this.setState({
          inlineToolbar: {
            show: true,
            position: {
              top: offsetTop,
              left: offsetLeft
            }
          }
        })
      }
    } else {
      this.setState({
        inlineToolbar: {
          show: false
        }
      })
    }
  }

  @autobind
  handleOnClick () {
    return this.editorRef.current.focus()
  }

  @autobind
  updateSelection () {
    const selectionRange = getSelectionRange()
    let selectedBlock
    if (selectionRange) {
      selectedBlock = getSelectedBlockElement(selectionRange)
    }
    this.setState({ selectedBlock, selectionRange })
  }

  @autobind
  onHandleKeyCommand (command) {
    const { editorState } = this.state
    const newState = RichUtils.handleKeyCommand(editorState, command)
    if (newState) {
      this.onChange(newState)
      return true
    }
    return false
  }

  @autobind
  handleToggleSideToolbar (blockType) {
    this.onChange(RichUtils.toggleBlockType(this.state.editorState, blockType))
  }

  @autobind
  // toggleInlineStyle (inlineStyle) {
  handleToggleInlineToolbar (inlineStyle) {
    this.onChange(RichUtils.toggleInlineStyle(this.state.editorState, inlineStyle))
  }

  updateLink (editorState, entityKey, url) {
    if (entityKey) {
      const selection = editorState.getSelection()

      if (url) {
        Entity.replaceData(entityKey, { url, target: '_blank', rel: 'noopener noreferrer' })
      } else {
        this.setState({
          editorState: RichUtils.toggleLink(editorState, selection, null)
        })
      }
    } else if (url) {
      entityKey = Entity.create('LINK', 'MUTABLE', { url, target: '_blank', rel: 'noopener noreferrer' })
      this.setState({
        editorState: RichUtils.toggleLink(
          editorState,
          editorState.getSelection(),
          entityKey
        )
      })
    }

    setTimeout(() => this.focus(), 25)
  }

  @autobind
  handleEditLink (e) {
    e.preventDefault()
    const { editorState } = this.state
    const { pageToggleOverlay } = this.props
    const editorContent = editorState.getCurrentContent()
    const selection = editorState.getSelection()
    const offset = selection.getAnchorOffset()
    const block = editorContent.getBlockForKey(selection.getAnchorKey())
    const selectedEntityKey = block.getEntityAt(offset)
    // const selectedText = block.getText().slice(selection.getStartOffset(), selection.getEndOffset());

    let url = null
    if (selectedEntityKey) {
      url = Entity.get(selectedEntityKey).getData().url
    }

    pageToggleOverlay(
      <LinkEditor
        {...{ pageToggleOverlay, url }}
        callback={this.updateLink.bind(this, editorState, selectedEntityKey)}
        cancel={this.focus}
      />
    )
  }

  @autobind
  handleRemoveLink (e) {
    e.preventDefault()
    const { editorState } = this.state
    const editorContent = editorState.getCurrentContent()
    const selection = editorState.getSelection()
    const offset = selection.getAnchorOffset()
    const block = editorContent.getBlockForKey(selection.getAnchorKey())
    const selectedEntityKey = block.getEntityAt(offset)
    this.updateLink(editorState, selectedEntityKey, null)
  }

  render () {
    const { editorState, selectedBlock, inlineToolbar } = this.state
    const { focus: handleOnClickTextEditor, onChange: handleOnEditorChange } = this
    let sideToolbarOffsetTop = 0

    if (selectedBlock) {
      const editor = this.containerRef.current
      const editorBounds = editor.getBoundingClientRect()
      const blockBounds = selectedBlock.getBoundingClientRect()

      sideToolbarOffsetTop = (blockBounds.bottom - editorBounds.top) - 31 // height of side toolbar
    }

    return (
      <div
        className='texteditor'
        ref={this.containerRef}
        onClick={handleOnClickTextEditor}
        onBlur={() => this.updateInlineToolbarPosition(null)}
        onFocus={() => setTimeout(this.updateInlineToolbarPosition, 25)}
      >
        <SideToolbar
          editorState={editorState}
          style={{ top: sideToolbarOffsetTop }}
          onToggle={this.handleToggleSideToolbar}
          blockTypes={this.props.blockTypes}
        />
        <InlineToolbar
          show={inlineToolbar.show}
          editorState={editorState}
          onToggle={this.handleToggleInlineToolbar}
          onLinkEdit={this.handleEditLink}
          onLinkRemove={this.handleRemoveLink}
          position={inlineToolbar.position}
        />
        <div className='text'>
          <Editor
            blockRendererFn={this.blockRenderer}
            blockStyleFn={this.blockStyler}
            editorState={editorState}
            handleKeyCommand={this.onHandleKeyCommand}
            onChange={handleOnEditorChange}
            placeholder={this.props.placeholder}
            spellCheck
            ref={this.editorRef}
          />
        </div>
      </div>
    )
  }
}
